import { LogModule } from '@/data/config';

import { loggerError, loggerInfo, loggerWarn } from '@/modules/logger';
import { isInvalid, isString } from '@/modules/validate';

import { checkWeChat, weChatLoadSDK } from './config';

/**
 * 小程序相关环境获取方法
 * @param {string} wechatUrl 默认华盛通域名链接
 * @returns
 */
export async function wxMiniProgramEnv(
  wechatUrl?: string
): Promise<{ miniprogram: boolean; [key: string]: unknown } | undefined> {
  // 校验是否在微信浏览器
  const { valid } = checkWeChat();
  if (!valid) return;

  const { miniProgram } = await weChatLoadSDK(wechatUrl);

  if (!miniProgram?.getEnv) {
    loggerWarn('wxMiniProgramEnv[miniProgram?.getEnv] 找不到', LogModule.WeChat);
    return;
  }

  return new Promise((resolve, reject) => {
    miniProgram
      .getEnv((res: { miniprogram: boolean; [key: string]: unknown }) => {
        loggerInfo(['in MiniProgram', res], LogModule.WeChat);
        resolve(res);
      })
      .catch((e: unknown) => {
        loggerError('wxMiniProgramEnv 执行错误' + (e ?? ''), LogModule.WeChat);
        reject(e);
      });
  });
}

/**
 * 向小程序发送消息，会在特定时机（小程序后退、组件销毁、分享）触发组件的message事件
 * @param data 请求参数
 * @param options 可选参数 - wechatUrl
 * @returns
 */
export async function wxMiniProgramPostMessage(data: unknown, options?: { wechatUrl?: string }) {
  // 校验是否在微信浏览器
  const { valid } = checkWeChat();
  if (!valid) return;

  const { miniProgram } = await weChatLoadSDK(options?.wechatUrl);

  loggerInfo(['wxMiniProgramPostMessage 参数', data], LogModule.WeChat);
  return miniProgram.postMessage(data);
}

/**
 * switchTab 跳转到 tabBar 页面，并关闭其他所有非 tabBar 页面
 *
 * reLaunch 关闭所有页面，打开到应用内的某个页面
 *
 * redirectTo 关闭当前页面，跳转到应用内的某个页面。但是不允许跳转到 tabbar 页面
 *
 * navigateTo 保留当前页面，跳转到应用内的某个页面。但是不能跳到 tabbar 页面
 *
 * navigateBack 关闭当前页面，返回上一页面或多级页面。
 */
export enum wxMiniProgramJumpType {
  switchTab = 'switchTab', // 跳转到 tabBar 页面，并关闭其他所有非 tabBar 页面
  reLaunch = 'reLaunch', // 关闭所有页面，打开到应用内的某个页面
  redirectTo = 'redirectTo', // 关闭当前页面，跳转到应用内的某个页面。但是不允许跳转到 tabbar 页面
  navigateTo = 'navigateTo', // 保留当前页面，跳转到应用内的某个页面。但是不能跳到 tabbar 页面
  navigateBack = 'navigateBack', // 关闭当前页面，返回上一页面或多级页面。
}

/**
 * 小程序跳转处理
 * @param options 参数与小程序接口一致
 * @param {wxMiniProgramJumpType} type
 * 可选 switchTab, reLaunch, redirectTo, navigateTo, navigateBack
 * 默认参数为 navigateTo
 * @param {object} config 可选 wechatUrl 调用微信SDK js 地址
 * @returns
 */
export async function wxMiniProgramJump(
  options:
    | {
        url: string;
        fail?: () => void;
        complete?: () => void;
        success?: () => void;
        events?: { [key: string]: unknown };
      }
    | string,
  type = wxMiniProgramJumpType.navigateTo,
  config?: { wechatUrl?: string }
): Promise<unknown> {
  if (!isInvalid(options)) {
    loggerWarn(['wxMiniProgramJump 无法获取options参数', options], LogModule.WeChat);
    return;
  }

  // 校验是否在微信浏览器
  const { valid } = checkWeChat(config?.wechatUrl);
  if (!valid) return;

  const { miniProgram } = await weChatLoadSDK();

  // 如果传入字符串，直接返回
  if (isString(options)) {
    return miniProgram[type]({ url: options });
  }

  return miniProgram[type](options);
}
